import { Component } from 'react';
import { get } from 'lodash';
import { lazyInject } from '../../../context/module';
import { SettingsService } from '../../../services/settings.service';
import { untilDestroyed } from '../../../utils/until-destroyed';
import { ConverterLayout, layoutStyles } from '../converter-layout/converter-layout';
import { CodeBlock } from '../converter-layout/code-block';
import { PersistedInput } from '../../../utils/persisted-input';

type Action = 'format' | 'minify' | 'validate';

interface EditorConfig {
    primaryAction: Action;
    language: string;
    availableActions: Action[];
}

const EDITOR_CONFIGS: Record<string, EditorConfig> = {
    'json-formatter':  { primaryAction: 'format',   language: 'json', availableActions: ['format', 'minify', 'validate'] },
    'json-minifier':   { primaryAction: 'minify',   language: 'json', availableActions: ['minify', 'format', 'validate'] },
    'validate-json':   { primaryAction: 'validate', language: 'json', availableActions: ['validate', 'format', 'minify'] },
    'css-formatter':   { primaryAction: 'format',   language: 'css',  availableActions: ['format', 'minify'] },
    'css-minifier':    { primaryAction: 'minify',   language: 'css',  availableActions: ['minify', 'format'] },
    'html-formatter':  { primaryAction: 'format',   language: 'html', availableActions: ['format', 'minify'] },
    'html-minifier':   { primaryAction: 'minify',   language: 'html', availableActions: ['minify', 'format'] },
    'js-formatter':    { primaryAction: 'format',   language: 'js',   availableActions: ['format', 'minify'] },
    'js-minifier':     { primaryAction: 'minify',   language: 'js',   availableActions: ['minify', 'format'] },
    'sql-formatter':   { primaryAction: 'format',   language: 'sql',  availableActions: ['format'] },
    'validate-yaml':   { primaryAction: 'validate', language: 'yaml', availableActions: ['validate'] },
    'validate-xml':    { primaryAction: 'validate', language: 'xml',  availableActions: ['validate'] },
};

interface EditorState {
    input: string;
    output: string;
    activeAction: Action;
    indentSize: number;
    error: string;
    valid: boolean | null;
    copiedField: string | null;
    showTabs: boolean;
}

// --- Processing functions ---

function jsonFormat(input: string, indent: number): string {
    return JSON.stringify(JSON.parse(input), null, indent);
}

function jsonMinify(input: string): string {
    return JSON.stringify(JSON.parse(input));
}

function jsonValidate(input: string): { valid: boolean; error: string } {
    try { JSON.parse(input); return { valid: true, error: '' }; }
    catch (e: any) { return { valid: false, error: e.message }; }
}

function cssFormat(input: string, indent: number): string {
    const pad = ' '.repeat(indent);
    let depth = 0;
    return input
        .replace(/\/\*[\s\S]*?\*\//g, m => m) // preserve comments
        .replace(/\s*{\s*/g, ' {\n')
        .replace(/\s*}\s*/g, '\n}\n')
        .replace(/\s*;\s*/g, ';\n')
        .split('\n')
        .map(line => {
            const trimmed = line.trim();
            if (!trimmed) return '';
            if (trimmed.startsWith('}')) depth = Math.max(0, depth - 1);
            const result = pad.repeat(depth) + trimmed;
            if (trimmed.endsWith('{')) depth++;
            return result;
        })
        .filter(Boolean)
        .join('\n');
}

function cssMinify(input: string): string {
    return input
        .replace(/\/\*[\s\S]*?\*\//g, '')
        .replace(/\s+/g, ' ')
        .replace(/\s*([{}:;,])\s*/g, '$1')
        .replace(/;}/g, '}')
        .trim();
}

function htmlFormat(input: string, indent: number): string {
    const pad = ' '.repeat(indent);
    const voidTags = new Set(['area','base','br','col','embed','hr','img','input','link','meta','source','track','wbr']);
    let depth = 0;
    const lines: string[] = [];

    input.replace(/>\s+</g, '><').trim().split(/(?=<)|(?<=>)/).forEach(token => {
        const t = token.trim();
        if (!t) return;
        if (t.startsWith('</')) {
            depth = Math.max(0, depth - 1);
            lines.push(pad.repeat(depth) + t);
        } else if (t.startsWith('<')) {
            lines.push(pad.repeat(depth) + t);
            const tagMatch = t.match(/^<(\w+)/);
            if (tagMatch && !voidTags.has(tagMatch[1].toLowerCase()) && !t.endsWith('/>')) {
                depth++;
            }
        } else {
            lines.push(pad.repeat(depth) + t);
        }
    });
    return lines.join('\n');
}

function htmlMinify(input: string): string {
    return input
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/>\s+</g, '><')
        .replace(/\s+/g, ' ')
        .trim();
}

async function jsFormat(input: string): Promise<string> {
    const mod: any = await import('js-beautify');
    const js_beautify = mod.js_beautify ?? mod.default?.js_beautify ?? mod.default;
    return js_beautify(input, { indent_size: 2 });
}

async function jsMinify(input: string): Promise<string> {
    // @ts-ignore — terser installed in Phase 2 implementation
    const { minify } = await import('terser');
    const result = await minify(input);
    return result.code || '';
}

async function sqlFormat(input: string): Promise<string> {
    // @ts-ignore — sql-formatter installed in Phase 2 implementation
    const { format } = await import('sql-formatter');
    return format(input);
}

async function yamlValidate(input: string): Promise<{ valid: boolean; error: string }> {
    try {
        const yaml = await import('js-yaml');
        yaml.load(input);
        return { valid: true, error: '' };
    } catch (e: any) { return { valid: false, error: e.message }; }
}

async function xmlValidate(input: string): Promise<{ valid: boolean; error: string }> {
    try {
        const { XMLValidator } = await import('fast-xml-parser');
        const result = XMLValidator.validate(input);
        if (result === true) return { valid: true, error: '' };
        return { valid: false, error: `Line ${result.err.line}: ${result.err.msg}` };
    } catch (e: any) { return { valid: false, error: e.message }; }
}

// --- Component ---

export default class CodeEditorComponent extends Component<{ feature?: string }, EditorState> {
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    @lazyInject(SettingsService) public settingsService: SettingsService;

    private get config(): EditorConfig {
        return EDITOR_CONFIGS[this.props.feature || ''] || EDITOR_CONFIGS['json-formatter'];
    }

    private get storageKey(): string {
        return `code-editor:${this.props.feature || 'default'}`;
    }
    private saveTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(props: { feature?: string }) {
        super(props);
        const cfg = EDITOR_CONFIGS[props.feature || ''] || EDITOR_CONFIGS['json-formatter'];
        // Restore the last typed input for this specific slug — JSON-formatter
        // and CSS-formatter share a component class but should not share state.
        const restored = PersistedInput.load(
            `code-editor:${props.feature || 'default'}`,
            { input: '', indentSize: 2 },
            (v): v is { input: string; indentSize: number } =>
                !!v && typeof v === 'object'
                && typeof (v as any).input === 'string'
                && typeof (v as any).indentSize === 'number',
        );
        this.state = {
            input: restored.input, output: '',
            activeAction: cfg.primaryAction,
            indentSize: restored.indentSize,
            error: '', valid: null, copiedField: null,
            // Default ON in initial render so the Format/Minify/Validate tabs
            // don't flicker missing while the settingsService observable warms
            // up. Feature flag still overrides if explicitly set to false.
            showTabs: true,
        };
    }

    // Lightweight syntax sanity check — counts unbalanced braces, brackets,
    // and parens. Not a full parser; only meaningful for languages where
    // those characters are actually structural. For markup formats (HTML,
    // XML) braces inside <script> / <style> bodies, JSX template strings,
    // and minified content trigger false positives — skip those entirely.
    // For JSON, lacking braces is usually obvious; the formatter's own
    // try/catch will surface real parse errors with line numbers.
    private static LINTABLE_LANGS = new Set(['js', 'javascript', 'ts', 'typescript', 'css', 'scss', 'jsx', 'tsx']);

    private lintInput(): string | null {
        const { input } = this.state;
        const { language } = this.config;
        if (!input || input.length < 3) return null;
        if (!CodeEditorComponent.LINTABLE_LANGS.has(language.toLowerCase())) return null;
        // Strip strings + comments so braces inside them don't throw the count off.
        let stripped = input
            .replace(/\/\/[^\n]*/g, '')
            .replace(/\/\*[\s\S]*?\*\//g, '')
            .replace(/"(?:[^"\\]|\\.)*"/g, '""')
            .replace(/'(?:[^'\\]|\\.)*'/g, "''")
            .replace(/`(?:[^`\\]|\\.)*`/g, '``');
        const pairs = [['{', '}'], ['[', ']'], ['(', ')']] as const;
        const issues: string[] = [];
        for (const [open, close] of pairs) {
            const opens = (stripped.match(new RegExp('\\' + open, 'g')) || []).length;
            const closes = (stripped.match(new RegExp('\\' + close, 'g')) || []).length;
            if (opens !== closes) {
                const diff = opens - closes;
                const label = open === '{' ? 'braces' : open === '[' ? 'brackets' : 'parens';
                issues.push(`${Math.abs(diff)} unclosed ${diff > 0 ? open : close} ${label}`);
            }
        }
        if (issues.length === 0) return null;
        return `Possible syntax issue: ${issues.join(', ')}. Output may be incomplete.`;
    }

    componentDidMount() {
        if (this.state.input) this.process(this.state.input);
        // showCodeStudioTabs flag was originally a staged-rollout gate.
        // It now ships ON for everyone — Format / Minify / Validate are
        // GA across every supported language. Stop reading the flag so the
        // QA environment (which still has it set to false in its settings
        // DB) doesn't override the initial-state default and hide the tabs.
    }

    componentDidUpdate(_: any, prev: EditorState) {
        if (prev.input !== this.state.input || prev.indentSize !== this.state.indentSize) {
            if (this.saveTimer) clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => {
                PersistedInput.save(this.storageKey, {
                    input: this.state.input,
                    indentSize: this.state.indentSize,
                });
            }, 500);
        }
    }

    componentWillUnmount() {
        // for untilDestroyed
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
        if (this.saveTimer) clearTimeout(this.saveTimer);
    }

    private scheduleProcess = (input: string) => {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.process(input), 300);
    };

    private async process(input: string) {
        if (!input.trim()) {
            this.setState({ output: '', error: '', valid: null });
            return;
        }

        const { activeAction, indentSize } = this.state;
        const { language } = this.config;

        try {
            if (activeAction === 'validate') {
                let result: { valid: boolean; error: string };
                if (language === 'json') result = jsonValidate(input);
                else if (language === 'yaml') result = await yamlValidate(input);
                else if (language === 'xml') result = await xmlValidate(input);
                else { result = { valid: false, error: 'Validation not supported for this language' }; }
                this.setState({ valid: result.valid, error: result.error, output: result.valid ? input : '' });
                return;
            }

            let output = '';
            if (activeAction === 'format') {
                if (language === 'json') output = jsonFormat(input, indentSize);
                else if (language === 'css') output = cssFormat(input, indentSize);
                else if (language === 'html') output = htmlFormat(input, indentSize);
                else if (language === 'js') output = await jsFormat(input);
                else if (language === 'sql') output = await sqlFormat(input);
            } else {
                if (language === 'json') output = jsonMinify(input);
                else if (language === 'css') output = cssMinify(input);
                else if (language === 'html') output = htmlMinify(input);
                else if (language === 'js') output = await jsMinify(input);
            }
            this.setState({ output, error: '', valid: null });
        } catch (e: any) {
            const msg = e.message || 'Processing error';
            if (msg.includes('is not a function') || msg.includes('Cannot find module') || msg.includes('Failed to fetch')) {
                this.setState({ output: '', error: `Package not available. Install the required dependency to use this feature.`, valid: null });
            } else {
                this.setState({ output: '', error: msg.split('\n')[0], valid: null });
            }
        }
    }

    handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const input = e.target.value;
        this.setState({ input });
        this.scheduleProcess(input);
    };

    setAction = (action: Action) => {
        this.setState({ activeAction: action, output: '', error: '', valid: null }, () => {
            if (this.state.input.trim()) this.process(this.state.input);
        });
    };

    copyOutput = () => {
        const text = this.state.activeAction === 'validate' && this.state.valid ? this.state.input : this.state.output;
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: 'output' });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    render() {
        const { input, output, activeAction, indentSize, error, valid, copiedField, showTabs } = this.state;
        const { availableActions, primaryAction, language } = this.config;
        const isCopied = copiedField === 'output';
        const actionLabels: Record<Action, string> = { format: 'Format', minify: 'Minify', validate: 'Validate' };

        const visibleActions = showTabs ? availableActions : [primaryAction];
        const renderTabBar = visibleActions.length > 1;

        const toolbar = (
            <>
                {renderTabBar && (
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        {visibleActions.map(action => (
                            <button
                                key={action}
                                className={`${layoutStyles.iconBtn} ${activeAction === action ? layoutStyles.iconBtnPrimary : ''}`}
                                onClick={() => this.setAction(action)}
                            >
                                {actionLabels[action]}
                            </button>
                        ))}
                    </div>
                )}
                {activeAction === 'format' && (
                    <div className={layoutStyles.formatField}>
                        <label htmlFor="indent">Indent</label>
                        <select id="indent" value={indentSize}
                            onChange={e => {
                                const size = Number(e.target.value);
                                this.setState({ indentSize: size }, () => {
                                    if (input.trim()) this.process(input);
                                });
                            }}>
                            <option value={2}>2 spaces</option>
                            <option value={4}>4 spaces</option>
                        </select>
                    </div>
                )}
            </>
        );

        // Validate mode: output panel shows the validation verdict instead of formatted text.
        const validateOutput = (
            <div className={layoutStyles.area}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '0.75rem', minHeight: 320 }}>
                {valid === null && !error && <>
                    <div style={{ fontSize: '2.5rem', opacity: 0.4 }}>✓</div>
                    <div style={{ color: '#64748b' }}>Paste {language.toUpperCase()} to validate</div>
                </>}
                {valid === true && !error && <>
                    <div style={{ fontSize: '3rem' }}>✓</div>
                    <div style={{ fontWeight: 600, color: '#065f46' }}>Valid {language.toUpperCase()}</div>
                </>}
                {(valid === false || error) && <>
                    <div style={{ fontSize: '3rem' }}>✗</div>
                    <div style={{ fontWeight: 600, color: '#991b1b' }}>Invalid</div>
                    {error && <div style={{ fontSize: '0.85rem', color: '#64748b', fontFamily: 'monospace' }}>{error}</div>}
                </>}
            </div>
        );

        const formatOutput = output ? (
            <CodeBlock code={output} language={language} wrapLines maxHeight={500} minHeight={320}
                ariaLabel={`${language.toUpperCase()} output`} />
        ) : (
            <textarea
                className={layoutStyles.area}
                value=""
                readOnly
                placeholder="Output will appear here…"
                spellCheck={false}
            />
        );

        const inputLines = input ? input.split('\n').length : 0;
        const outputLines = output ? output.split('\n').length : 0;
        const lintWarning = activeAction === 'format' || activeAction === 'minify' ? this.lintInput() : null;
        const errorOrLint = error || lintWarning;

        return (
            <ConverterLayout
                toolbar={toolbar}

                inputLabel={`Input (${language.toUpperCase()})`}
                inputMeta={input ? `${input.length.toLocaleString()} chars · ${inputLines.toLocaleString()} line${inputLines === 1 ? '' : 's'}` : undefined}
                inputArea={
                    <textarea
                        className={layoutStyles.area}
                        value={input}
                        onChange={this.handleInput}
                        placeholder={`Paste ${language.toUpperCase()} here…`}
                        spellCheck={false}
                    />
                }

                outputLabel={activeAction === 'validate' ? 'Validation' : 'Output'}
                outputMeta={activeAction !== 'validate' && output
                    ? `${output.length.toLocaleString()} chars · ${outputLines.toLocaleString()} line${outputLines === 1 ? '' : 's'}`
                    : undefined}
                outputArea={activeAction === 'validate' ? validateOutput : formatOutput}
                outputActions={activeAction !== 'validate' ? (
                    <button
                        className={`${layoutStyles.iconBtn} ${isCopied ? layoutStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput}
                        disabled={!output}
                    >
                        {isCopied ? '✓ Copied' : '📋 Copy'}
                    </button>
                ) : undefined}

                error={activeAction !== 'validate' ? errorOrLint : null}
            />
        );
    }
}
